import { createContext, useEffect, useState, type ReactNode } from 'react'

import { getMyAccount, type TalentAccount } from '@/services/account'
import { useAuth } from '@/app/use-auth'

export interface AccountContextValue {
  account: TalentAccount | null
  /** Non-empty when `GET /v1/me` failed — so consumers can say "failed to load" instead of
   * rendering blank fields that read as an empty account. */
  error: string
  setAccount(account: TalentAccount): void
}

export const AccountContext = createContext<AccountContextValue | undefined>(undefined)

/**
 * One `GET /v1/me` for the whole app, not one per page. AppHeader, ProfilePage and
 * AccountSettingsPage each used to fetch and hold their own copy — harmless for reads, but it
 * meant an avatar upload on the profile page could never be seen by the header's own copy without
 * a full reload. `setAccount` here is the same fix `setRealProfile` is for `cvs[]`: the write path
 * updates the ONE place every reader shares, instead of a component-local echo of it.
 */
export function AccountProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<TalentAccount | null>(null)
  const [error, setError] = useState('')
  // `isInitializing` before anything else: this provider sits INSIDE AuthProvider, so React
  // fires this effect before AuthProvider's own bootstrap effect (children commit before
  // parents) — firing the real fetch immediately raced the in-memory access token every time,
  // got a bearer-less 401, and fell back to mock for the rest of the session with no retry.
  const { isInitializing, isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (isInitializing) return
    // `isInitializing` only ever flips true→false ONCE per tab (see the comment above) — keying
    // the effect on it alone meant a logout+login as someone else, with no full page reload in
    // between, never re-ran this fetch: every reader (AppHeader, ProfilePage,
    // AccountSettingsPage) kept showing whichever account was logged in FIRST, for the rest of
    // the tab's life. `user?.userId` is the real identity of "who is this a fetch for" — a
    // different value (including logging out, `undefined`) must always re-run it.
    if (!isAuthenticated) {
      setAccount(null)
      setError('')
      return
    }
    let cancelled = false
    setError('')
    getMyAccount()
      .then(({ user: fetchedUser }) => {
        if (!cancelled) setAccount(fetchedUser)
      })
      .catch(() => {
        // Surfaced through the context — with no fallback data left, a swallowed failure renders
        // every consumer blank, indistinguishable from a brand-new account.
        if (!cancelled) setError('Could not load your account details — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [isInitializing, isAuthenticated, user?.userId])

  return <AccountContext.Provider value={{ account, error, setAccount }}>{children}</AccountContext.Provider>
}
